/**
 * Knowledge Base API Client
 *
 * Covers KB collections, articles, FAQ, versions, import/export.
 * Routes: /api/kb/* — disabled in nginx (feature `ss-api`) when ss-api isn't
 * deployed. Callers should `catch` `FeatureDisabledError` to render an empty
 * state.
 */

import { apiFetch } from './_http';

const BASE = '/api/kb';

// ── Types ────────────────────────────────────────────────────────────────────

export interface KBCollection {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  parent_id: string | null;
  sort_order: number;
  visible: boolean;
  article_count?: number;
  created_at: string;
  updated_at: string;
}

export interface KBArticle {
  id: string;
  title: string;
  slug: string;
  content: string;
  collection_id: string | null;
  status: 'draft' | 'published' | 'archived';
  site: 'success' | 'support' | 'both';
  author_id: string | null;
  visible: boolean;
  views_count?: number;
  helpfulness_avg?: number;
  org_id?: string | null;
  org_name?: string | null;
  assignment_id?: string;
  collection?: { slug: string; name: string } | null;
  created_at: string;
  updated_at: string;
}

export interface KBArticleCollection {
  id: string;
  article_id: string;
  collection_id: string;
  is_primary: boolean;
  sort_order: number;
  slug: string | null;
  visibility: string | null;
  required_roles: string[] | null;
  org_id: string | null;
  created_at: string;
}

export interface OrgOption {
  id: string;
  name: string;
  slug: string | null;
  logo_url: string | null;
}

export interface KBArticleVersion {
  id: string;
  article_id: string;
  version_number: number;
  content: string;
  author_id: string | null;
  created_at: string;
}

export interface KBFaq {
  id: string;
  question: string;
  answer: string;
  collection_id: string | null;
  sort_order: number;
  visible: boolean;
  created_at: string;
  updated_at: string;
}

// ── API ──────────────────────────────────────────────────────────────────────

export const kbApi = {
  // Collections
  async listCollections(): Promise<{ collections: KBCollection[] }> {
    const res = await apiFetch<{ collections?: KBCollection[]; data?: { collections: KBCollection[] } }>(`${BASE}/collections`);
    // The public KB endpoint wraps in { data: { collections } }; normalise both shapes.
    return { collections: res.collections ?? res.data?.collections ?? [] };
  },

  async createCollection(data: { name: string; description?: string; parent_id?: string }): Promise<KBCollection> {
    return apiFetch(`${BASE}/collections`, { method: 'POST', body: JSON.stringify(data) });
  },

  // Articles (via content routes — articles are content items with KB metadata)
  async listArticles(params?: { collection_id?: string; status?: string; search?: string }): Promise<{ articles: KBArticle[] }> {
    const qs = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v) qs.set(k === 'search' ? 'q' : k, v);
      });
    }
    const query = qs.toString() ? `?${qs}` : '';
    return apiFetch(`${BASE}/articles${query}`);
  },


  // Versions
  async listVersions(articleId: string): Promise<{ versions: KBArticleVersion[] }> {
    return apiFetch(`${BASE}/articles/${articleId}/versions`);
  },

  async restoreVersion(articleId: string, versionId: string): Promise<KBArticle> {
    return apiFetch(`${BASE}/articles/${articleId}/versions/${versionId}/restore`, { method: 'POST' });
  },

  // FAQ
  async listFaqs(collectionId?: string): Promise<{ faqs: KBFaq[] }> {
    const qs = collectionId ? `?collection_id=${collectionId}` : '';
    return apiFetch(`${BASE}/faqs${qs}`);
  },

  // Organisations (admin)
  async listOrganisations(): Promise<{ organisations: OrgOption[] }> {
    return apiFetch(`${BASE}/organisations`);
  },

  // Article assignment
  async assignArticle(articleId: string, data: {
    collection_id: string;
    is_primary: boolean;
    sort_order?: number;
    slug?: string | null;
    visibility?: string | null;
    org_id?: string | null;
  }): Promise<KBArticleCollection> {
    return apiFetch(`${BASE}/articles/${articleId}/collections`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateAssignmentOrg(assignmentId: string, orgId: string | null): Promise<KBArticleCollection> {
    return apiFetch(`${BASE}/assignments/${assignmentId}/org`, {
      method: 'PUT',
      body: JSON.stringify({ org_id: orgId }),
    });
  },

  // ── Article create / edit / update ──────────────────────────────────────────

  // Create article (returns the new editor_documents row)
  async createArticle(data: {
    title: string;
    content?: string;
    doc_type?: 'guide' | 'note' | 'document';
    target_site?: 'success' | 'support' | 'both';
    tags?: string[];
  }): Promise<{ id: string; title: string; doc_type: string; status: string; tags: string[]; created_at: string; updated_at: string }> {
    return apiFetch(`${BASE}/articles`, { method: 'POST', body: JSON.stringify(data) });
  },

  /** Fetch a single article by ID with full content for editing (admin only). */
  async getArticleForEdit(id: string): Promise<KBArticle & { slug: string | null; collection_id: string | null; collection_name: string | null; assignment_id: string | null; org_id: string | null }> {
    return apiFetch(`${BASE}/articles/${id}/admin`);
  },

  // Update article body/title/status (by editor_documents.id).
  // PATCH /api/kb/articles/:id (kb-article-versions.ts) expects { body, title, change_summary }.
  // content → body field; metadata (title/status) as top-level fields.
  async updateArticle(id: string, data: {
    title?: string;
    content?: string;
    status?: 'draft' | 'published' | 'archived';
  }): Promise<{ data: { updated_at?: string; version_number?: number }; message: string }> {
    const payload: Record<string, unknown> = {};
    if (data.content !== undefined) payload.body = data.content;
    if (data.title !== undefined) payload.title = data.title;
    if (data.status !== undefined) payload.status = data.status;
    return apiFetch(`${BASE}/articles/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
  },

  // Import/Export
  async exportZip(): Promise<Blob> {
    const res = await fetch(`${BASE}/export`, { credentials: 'include' });
    if (!res.ok) throw new Error('Export failed');
    return res.blob();
  },

  async importZip(file: File): Promise<{ imported: number; errors: string[] }> {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${BASE}/import`, { method: 'POST', body: form, credentials: 'include' });
    if (!res.ok) throw new Error('Import failed');
    return res.json();
  },
};
